import React from "react";
import { 
  XMarkIcon, 
  UserGroupIcon, 
  UserIcon, 
  BriefcaseIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  PencilIcon,
  CheckCircleIcon
} from "@heroicons/react/24/outline";

const LeadDetailsModal = ({
  show,
  selectedLead,
  onClose,
  isDarkMode,
  employeeConfig,
  formatDate,
  setCustomerFormData,
  setShowLeadDetailsModal,
  setShowEditCustomerModal,
  setShowFollowUpModal,
  handleEditFollowUp,
  setCustomerAdminFormData,
  setShowConvertToCustomerModal,
  setSelectedLead
}) => {
  if (!show || !selectedLead) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => {
          onClose();
          setSelectedLead(null);
        }}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="px-4 pt-4 pb-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-opacity-10 rounded-full flex items-center justify-center">
                  <UserGroupIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selectedLead.company_name}</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{selectedLead.industry}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  onClose();
                  setSelectedLead(null);
                }}
                className="hover:opacity-80"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left Column - Lead Information */}
              <div className="lg:col-span-2 space-y-4">
                {/* Status & Lead Info */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Information</h4>
                    <button
                      onClick={() => {
                        setCustomerFormData({
                          company_name: selectedLead.company_name || '',
                          industry: selectedLead.industry || 'technology',
                          company_size: selectedLead.company_size || (employeeConfig.length > 0 ? employeeConfig[0].name : ''),
                          price: selectedLead.price || '',
                          primary_contact_name: selectedLead.primary_contact_name || '',
                          primary_contact_email: selectedLead.primary_contact_info?.email || '',
                          primary_contact_phone: selectedLead.primary_contact_info?.phone || '',
                          primary_contact_title: selectedLead.primary_contact_title || '',
                          lead_source: selectedLead.lead_source || '',
                          status: selectedLead.status || 'New Lead',
                          notes: selectedLead.notes || '',
                          tags: selectedLead.tags || [],
                          conversion_probability: selectedLead.conversion_probability || 0
                        });
                        setShowLeadDetailsModal(false);
                        setShowEditCustomerModal(true);
                      }}
                      className="flex items-center space-x-1 hover:opacity-80 text-sm font-medium transition-colors"
                    >
                      {/* Intentionally commented out as per original code */}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Status</p>
                      <span className="mt-1 px-2 py-0.5 inline-flex text-xs font-semibold rounded-full" style={{
                        backgroundColor: selectedLead.status === 'Confirmed' ? (isDarkMode ? 'rgba(20, 83, 45, 0.2)' : '#a7f3d0') :
                                        selectedLead.status === 'Qualified' ? (isDarkMode ? 'rgba(30, 58, 138, 0.2)' : '#bfdbfe') :
                                        selectedLead.status === 'Contacted' ? (isDarkMode ? 'rgba(133, 77, 14, 0.2)' : '#fde68a') :
                                        selectedLead.status === 'New Lead' ? (isDarkMode ? 'rgba(75, 85, 99, 0.2)' : '#d1d5db') :
                                        selectedLead.status === 'Lost' ? (isDarkMode ? 'rgba(153, 27, 27, 0.2)' : '#fca5a5') :
                                        (isDarkMode ? 'rgba(30, 58, 138, 0.2)' : '#bfdbfe'),
                        color: selectedLead.status === 'Confirmed' ? (isDarkMode ? '#4ade80' : '#047857') :
                              selectedLead.status === 'Qualified' ? (isDarkMode ? '#60a5fa' : '#1e40af') :
                              selectedLead.status === 'Contacted' ? (isDarkMode ? '#fbbf24' : '#92400e') :
                              selectedLead.status === 'New Lead' ? (isDarkMode ? '#d1d5db' : '#1f2937') :
                              selectedLead.status === 'Lost' ? (isDarkMode ? '#f87171' : '#991b1b') :
                              (isDarkMode ? '#60a5fa' : '#1e40af')
                      }}>
                        {selectedLead.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Company Size</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mt-0.5">{selectedLead.company_size}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Lead Source</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mt-0.5">{selectedLead.lead_source || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Conversion Probability</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mt-0.5">{selectedLead.conversion_probability || 0}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Created Date</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                        {selectedLead.created_at ? formatDate(selectedLead.created_at) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Last Updated</p>
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                        {selectedLead.updated_at ? formatDate(selectedLead.updated_at) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Primary Contact */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Primary Contact</h4>
                  <div className="space-y-3">
                    {/* First Row - Name and Job Title */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Name</p>
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100">{selectedLead.primary_contact_name}</p>
                        </div>
                      </div>
                      {selectedLead.primary_contact_title && (
                        <div className="flex items-center space-x-2">
                          <BriefcaseIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Job Title</p>
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">{selectedLead.primary_contact_title}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Second Row - Email and Phone */}
                    <div className="grid grid-cols-2 gap-3">
                      {selectedLead.primary_contact_info?.email && (
                        <div className="flex items-center space-x-2">
                          <EnvelopeIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Email</p>
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">{selectedLead.primary_contact_info.email}</p>
                          </div>
                        </div>
                      )}
                      {selectedLead.primary_contact_info?.phone && (
                        <div className="flex items-center space-x-2">
                          <PhoneIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Phone</p>
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">{selectedLead.primary_contact_info.phone}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedLead.notes && (
                  <div className="bg-yellow-50 dark:bg-gray-800 border border-yellow-200 dark:border-gray-700 rounded-lg p-3">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Notes</h4>
                    <p className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{selectedLead.notes}</p>
                  </div>
                )}

                {/* Tags */}
                {selectedLead.tags && selectedLead.tags.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedLead.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Follow-ups & Actions */}
              <div className="space-y-3">
                {/* Follow-ups */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white">Follow-ups</h4>
                    <button
                      onClick={() => {
                        setShowLeadDetailsModal(false);
                        setShowFollowUpModal(true);
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                    >
                      + Add
                    </button>
                  </div>
                  
                  {selectedLead.follow_ups && selectedLead.follow_ups.length > 0 ? (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {selectedLead.follow_ups.map((followUp, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-gray-900 dark:text-white text-xs">{followUp.follow_up_type}</span>
                            <div className="flex items-center space-x-1">
                              <span className="px-1.5 py-0.5 text-[10px] rounded-full" style={{
                                backgroundColor: followUp.status === 'Completed' ? (isDarkMode ? 'rgba(20, 83, 45, 0.2)' : '#a7f3d0') :
                                                followUp.status === 'Scheduled' ? (isDarkMode ? 'rgba(30, 58, 138, 0.2)' : '#bfdbfe') :
                                                followUp.status === 'Cancelled' ? (isDarkMode ? 'rgba(153, 27, 27, 0.2)' : '#fca5a5') :
                                                (isDarkMode ? 'rgba(133, 77, 14, 0.2)' : '#fde68a'),
                                color: followUp.status === 'Completed' ? (isDarkMode ? '#4ade80' : '#047857') :
                                      followUp.status === 'Scheduled' ? (isDarkMode ? '#60a5fa' : '#1e40af') :
                                      followUp.status === 'Cancelled' ? (isDarkMode ? '#f87171' : '#991b1b') :
                                      (isDarkMode ? '#fbbf24' : '#92400e')
                              }}>
                                {followUp.status}
                              </span>
                              {followUp.outcome !== 'Engaged' && (
                                <button
                                  onClick={() => handleEditFollowUp(followUp, index)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-1"
                                  title="Edit follow-up"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                          <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">
                            <CalendarIcon className="w-2.5 h-2.5 inline mr-0.5" />
                            {followUp.scheduled_date ? formatDate(followUp.scheduled_date) : 'Not scheduled'}
                          </p>
                          {followUp.notes && (
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 mt-1">{followUp.notes}</p>
                          )}
                          {followUp.outcome && (
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5">
                              <span className="font-medium text-gray-700 dark:text-gray-300">Outcome:</span> {followUp.outcome}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">No follow-ups scheduled</p>
                  )}
                </div>

                {/* Next Follow-up */}
                {selectedLead.next_follow_up_date && (
                  <div className="rounded-lg p-2" style={{
                    backgroundColor: isDarkMode ? 'rgba(88, 28, 135, 0.2)' : '#faf5ff',
                    border: `1px solid ${isDarkMode ? '#7e22ce' : '#e9d5ff'}`
                  }}>
                    <h4 className="text-xs font-semibold mb-0.5" style={{
                      color: isDarkMode ? '#d8b4fe' : '#581c87'
                    }}>Next Follow-up</h4>
                    <p className="text-[10px]" style={{
                      color: isDarkMode ? '#c084fc' : '#6b21a8'
                    }}>
                      <CalendarIcon className="w-3 h-3 inline mr-0.5" style={{color: 'inherit'}} />
                      {formatDate(selectedLead.next_follow_up_date)}
                    </p>
                  </div>
                )}

                {/* Conversion Status */}
                {selectedLead.converted_to_user ? (
                  <div className="rounded-lg p-2" style={{
                    backgroundColor: isDarkMode ? 'rgba(20, 83, 45, 0.2)' : '#f0fdf4',
                    border: `1px solid ${isDarkMode ? '#15803d' : '#bbf7d0'}`
                  }}>
                    <div className="flex items-center space-x-1.5 mb-1">
                      <CheckCircleIcon className="w-4 h-4" style={{
                        color: isDarkMode ? '#4ade80' : '#16a34a'
                      }} />
                      <h4 className="text-xs font-semibold" style={{
                        color: isDarkMode ? '#86efac' : '#14532d'
                      }}>Converted to Customer</h4>
                    </div>
                    {selectedLead.conversion_date && (
                      <p className="text-[8px]" style={{
                        color: isDarkMode ? '#4ade80' : '#166534'
                      }}>
                        Converted on: {formatDate(selectedLead.conversion_date)}
                      </p>
                    )}
                    {selectedLead.converted_user_id && (
                      <p className="text-[8px] mt-0.5" style={{
                        color: isDarkMode ? '#4ade80' : '#166534'
                      }}>
                        User ID: {selectedLead.converted_user_id}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    {(() => {
                      // Check if there's any 'Engaged' outcome follow-up
                      const hasEngagedFollowUp = selectedLead.follow_ups && selectedLead.follow_ups.some(
                        followUp => followUp.outcome === 'Engaged'
                      );
                      
                      // Show button only when has Engaged outcome follow-up
                      return hasEngagedFollowUp && (
                        <button
                          onClick={() => {
                            // Prefill customer admin form with lead data
                            setCustomerAdminFormData({
                              firstname: selectedLead.primary_contact_name?.split(' ')[0] || '',
                              lastname: selectedLead.primary_contact_name?.split(' ').slice(1).join(' ') || '',
                              email: selectedLead.primary_contact_info?.email || '',
                              password: '',
                              phone: selectedLead.primary_contact_info?.phone || '',
                              customername: selectedLead.company_name || '',
                              location: '',
                              department: '',
                              employeecount: selectedLead.company_size || '',
                              price: selectedLead.price || ''
                            });
                            setShowLeadDetailsModal(false);
                            setShowConvertToCustomerModal(true);
                          }}
                          className="w-full bg-green-600 dark:bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors font-medium"
                        >
                          Convert to Customer
                        </button>
                      );
                    })()}
                  </div>
                )}

                {/* Quick Actions */}
                <div className="border-t dark:border-gray-700 pt-2">
                  <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-1.5">Quick Actions</h4>
                  <div className="space-y-1">
                    {!selectedLead.converted_to_user && selectedLead.status !== 'Confirmed' && (
                      <button
                        onClick={() => {
                          setCustomerFormData({
                            company_name: selectedLead.company_name || '',
                            industry: selectedLead.industry || 'technology',
                            company_size: selectedLead.company_size || (employeeConfig.length > 0 ? employeeConfig[0].name : ''),
                            price: selectedLead.price || '',
                            primary_contact_name: selectedLead.primary_contact_name || '',
                            primary_contact_email: selectedLead.primary_contact_info?.email || '',
                            primary_contact_phone: selectedLead.primary_contact_info?.phone || '',
                            primary_contact_title: selectedLead.primary_contact_title || '',
                            lead_source: selectedLead.lead_source || '',
                            status: selectedLead.status || 'New Lead',
                            notes: selectedLead.notes || '',
                            tags: selectedLead.tags || [],
                            conversion_probability: selectedLead.conversion_probability || 0
                          });
                          setShowLeadDetailsModal(false);
                          setShowEditCustomerModal(true);
                        }}
                        className="w-full text-left px-3 py-2 text-sm bg-purple-50 dark:bg-gray-700 text-purple-700 dark:text-white rounded hover:bg-purple-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        <PencilIcon className="w-4 h-4 inline mr-2 text-purple-700 dark:text-white" />
                        Edit Lead Details
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowLeadDetailsModal(false);
                        setShowFollowUpModal(true);
                      }}
                      className="w-full text-left px-3 py-2 text-sm rounded transition-colors"
                      style={{
                        backgroundColor: isDarkMode ? '#374151' : '#eff6ff',
                        color: isDarkMode ? '#ffffff' : '#1d4ed8'
                      }}
                    >
                      <CalendarIcon className="w-3 h-3 inline mr-1.5" style={{color: 'inherit'}} />
                      Schedule Follow-up
                    </button>
                    {selectedLead.primary_contact_info?.email && (
                      <button
                        onClick={() => {
                          window.location.href = `mailto:${selectedLead.primary_contact_info.email}`;
                        }}
                        className="w-full text-left px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-white rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        <EnvelopeIcon className="w-4 h-4 inline mr-2 text-gray-700 dark:text-white" />
                        Send Email
                      </button>
                    )}
                    {selectedLead.primary_contact_info?.phone && (
                      <button
                        onClick={() => {
                          window.location.href = `tel:${selectedLead.primary_contact_info.phone}`;
                        }}
                        className="w-full text-left px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-white rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        <PhoneIcon className="w-4 h-4 inline mr-2 text-gray-700 dark:text-white" />
                        Call Contact
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  onClose();
                  setSelectedLead(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsModal;
